import React, { useState, useEffect } from "react";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import "jspdf-autotable";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import GenerateImage from "./GenerateImage";

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [creditorFilter, setCreditorFilter] = useState("");
  const [debitorFilter, setDebitorFilter] = useState("");
  const [amountFilter, setAmountFilter] = useState("");
  const [dateFilter, setDateFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [creditors, setCreditors] = useState([]);
  const [debitors, setDebitors] = useState([]);
  const [showGenerate, setShowGenerate] = useState(false);
  const [prefillData, setPrefillData] = useState(null);

  const [sortConfig, setSortConfig] = useState({
    key: "custom_transaction_id",
    direction: "desc",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    fetchTransactions();
    fetchCreditors();
    fetchDebitors();
  }, []);

  useEffect(() => {
    fetchTransactions();
    setCurrentPage(1);
  }, [creditorFilter, debitorFilter, amountFilter, dateFilter, statusFilter]);

  const fetchTransactions = async () => {
    try {
      const params = {};
      if (creditorFilter) params.creditor = creditorFilter;
      if (debitorFilter) params.debitor = debitorFilter;
      if (amountFilter) params.amount = amountFilter;
      if (statusFilter) params.status = statusFilter;
      if (dateFilter) {
        const formattedDate = dateFilter.toISOString().split("T")[0];
        params.date = formattedDate;
      }

      const response = await axios.get("/api/get_transactions.php", { params });
      setTransactions(response.data || []);
    } catch (error) {
      console.error("Error fetching transactions:", error);
    }
  };

  const fetchCreditors = async () => {
    try {
      const res = await axios.get("/api/creditors.php");
      setCreditors(res.data || []);
    } catch (error) {
      console.error("Error fetching creditors:", error);
    }
  };

  const fetchDebitors = async () => {
    try {
      const res = await axios.get("/api/debitors.php");
      setDebitors(res.data || []);
    } catch (error) {
      console.error("Error fetching debitors:", error);
    }
  };

  const handleRemarkChange = async (id, newRemark) => {
    try {
      await axios.post("/api/update-remark.php", { id, remarks: newRemark });
      setTransactions((prev) =>
        prev.map((tx) => (tx.id === id ? { ...tx, remarks: newRemark } : tx))
      );
    } catch (err) {
      console.error("Failed to update remark", err);
    }
  };

  const updateStatus = async (id, newStatus) => {
    if (
      !["Committed", "Cancelled", "Completed", "Part Payment"].includes(
        newStatus
      )
    )
      return;

    try {
      await axios.post("/api/update-status.php", { id, status: newStatus });
      setTransactions((prev) =>
        prev.map((tx) => (tx.id === id ? { ...tx, status: newStatus } : tx))
      );
    } catch (err) {
      console.error("Failed to update status", err);
    }
  };

  const findRootTransaction = (tx) => {
    if (!tx.parent_transaction_id) return tx;
    const parent = transactions.find((t) => t.id === tx.parent_transaction_id);
    return parent ? findRootTransaction(parent) : tx;
  };

  const handlePartPayment = async (tx) => {
    try {
      const rootTx = findRootTransaction(tx);
      const res = await axios.post("/api/check_transaction_id.php", {
        parent_id: rootTx.id,
      });

      if (!res.data.success) {
        alert("Failed to generate next transaction ID.");
        return;
      }

      const customId = res.data.next_id;

      // Prefer IDs from transaction, fallback to code/name
      let creditorObj =
        creditors.find((c) => c.id === tx.creditor_id) ||
        creditors.find((c) => c.code === tx.creditorCode) ||
        creditors.find((c) => c.name === tx.creditorName);

      let debitorObj =
        debitors.find((d) => d.id === tx.debitor_id) ||
        debitors.find((d) => d.code === tx.debitorCode) ||
        debitors.find((d) => d.name === tx.debitorName);

      if (!creditorObj || !debitorObj) {
        alert(
          "Could not match creditor/debitor for this transaction. Please verify master data."
        );
        return;
      }

      setPrefillData({
        creditorId: creditorObj.id,
        debitorId: debitorObj.id,
        mode_of_transfer: tx.mode_of_transfer,
        bank_name: tx.bank_name,
        branch_name: tx.branch,
        ifsc_code: tx.ifsc_code,
        account_holder_name: tx.account_holder_name,
        account_number: tx.account_number,
        parent_transaction_id: rootTx.id,
        custom_transaction_id: customId,
        amount: "", // user will enter new amount
      });

      setShowGenerate(true);
    } catch (err) {
      console.error("Part Payment failed:", err);
      alert("Error while generating Part Payment.");
    }
  };

  const sortedTransactions = [...transactions].sort((a, b) => {
    if (!sortConfig.key) return 0;
    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];

    // numeric sort for amount & id
    if (sortConfig.key === "amount" || sortConfig.key === "id") {
      aValue = Number(aValue) || 0;
      bValue = Number(bValue) || 0;
    }

    if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  const paginatedTransactions = sortedTransactions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(sortedTransactions.length / itemsPerPage);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction:
        prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "asc" ? " 🔼" : " 🔽";
  };

  const copyImageToClipboard = async (dataUrl) => {
    try {
      if (!navigator.clipboard || !window.ClipboardItem) {
        alert("Clipboard image copying is not supported in this browser.");
        return;
      }
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      const pngBlob = await convertToPng(blob);
      const clipboardItem = new ClipboardItem({ "image/png": pngBlob });
      await navigator.clipboard.write([clipboardItem]);
      alert("Image copied to clipboard!");
    } catch (err) {
      console.error("Copy failed:", err);
      alert("Copy failed. Try right-clicking the image.");
    }
  };

  const convertToPng = (blob) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(blob);

      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);

        canvas.toBlob(
          (pngBlob) => {
            URL.revokeObjectURL(url);
            resolve(pngBlob);
          },
          "image/png"
        );
      };

      img.onerror = reject;
      img.src = url;
    });
  };

  const headers = [
    { label: "Custom Transaction ID", key: "custom_transaction_id" },
    { label: "Creditor Name", key: "creditorName" },
    { label: "Creditor Code", key: "creditorCode" },
    { label: "Debitor Name", key: "debitorName" },
    { label: "Debitor Code", key: "debitorCode" },
    { label: "Amount", key: "amount" },
    { label: "Mode", key: "mode_of_transfer" },
    { label: "Bank", key: "bank_name" },
    { label: "Branch", key: "branch" },
    { label: "IFSC", key: "ifsc_code" },
    { label: "Account Holder", key: "account_holder_name" },
    { label: "Account Number", key: "account_number" },
    { label: "Status", key: "status" },
    { label: "Date", key: "date" },
    { label: "Remarks", key: "remarks" },
  ];

  const renderStatusBadge = (status) => {
    const base = "badge";
    switch (status) {
      case "Committed":
        return <span className={`${base} bg-success`}>Committed</span>;
      case "Cancelled":
        return <span className={`${base} bg-danger`}>Cancelled</span>;
      case "Completed":
        return <span className={`${base} bg-primary`}>Completed</span>;
      case "Part Payment":
        return <span className={`${base} bg-warning text-dark`}>Part Payment</span>;
      default:
        return <span className={base}>{status}</span>;
    }
  };

  return (
    <div className="ac-container mt-4">
      <h2 className="ac-title">Transaction History</h2>

      <div className="d-flex justify-content-end gap-1 mb-3">
        <CSVLink
          data={transactions}
          headers={headers}
          filename="transactions.csv"
          className="btn btn-secondary"
        >
          Export CSV
        </CSVLink>
      </div>

      {/* Filters */}
      <div className="row mb-3 g-2">
        <div className="col">
          <input
            type="text"
            className="form-control"
            placeholder="Filter by Creditor"
            value={creditorFilter}
            onChange={(e) => setCreditorFilter(e.target.value)}
          />
        </div>
        <div className="col">
          <input
            type="text"
            className="form-control"
            placeholder="Filter by Debitor"
            value={debitorFilter}
            onChange={(e) => setDebitorFilter(e.target.value)}
          />
        </div>
        <div className="col">
          <input
            type="text"
            className="form-control"
            placeholder="Filter by Amount"
            value={amountFilter}
            onChange={(e) => setAmountFilter(e.target.value)}
          />
        </div>
        <div className="col">
          <DatePicker
            selected={dateFilter}
            onChange={(date) => setDateFilter(date)}
            className="form-control"
            placeholderText="Filter by Date"
          />
        </div>
        <div className="col">
          <select
            className="form-control"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="Committed">Committed</option>
            <option value="Cancelled">Cancelled</option>
            <option value="Completed">Completed</option>
            <option value="Part Payment">Part Payment</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="table-responsive">
        <table
          className="table table-bordered table-hover table-sm align-middle text-center"
          id="transaction-table"
          style={{ fontSize: "14px" }}
        >
          <thead>
            <tr>
              <th onClick={() => handleSort("custom_transaction_id")}>
                Transaction ID {renderSortIcon("custom_transaction_id")}
              </th>
              <th>Creditor</th>
              <th>Debitor</th>
              <th onClick={() => handleSort("amount")}>
                Amount {renderSortIcon("amount")}
              </th>
              <th>Status</th>
              <th>Date</th>
              <th>Bank</th>
              <th style={{ minWidth: "120px" }}>Branch</th>
              <th>IFSC</th>
              <th>Holder</th>
              <th>Account</th>
              <th>Image</th>
              <th>Remarks</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedTransactions.map((tx) => (
              <tr
                key={tx.id}
                className={tx.status === "Committed" ? "table-success" : ""}
              >
                <td>{tx.custom_transaction_id}</td>
                <td>
                  <strong>{tx.creditorName}</strong>
                  <div style={{ fontSize: "12px", color: "#555" }}>
                    {tx.creditorCode}
                  </div>
                </td>
                <td>
                  <strong>{tx.debitorName}</strong>
                  <div style={{ fontSize: "12px", color: "#555" }}>
                    {tx.debitorCode}
                  </div>
                </td>
                <td>{tx.amount}</td>
                <td>{renderStatusBadge(tx.status)}</td>
                <td>{tx.date}</td>
                <td>{tx.bank_name}</td>
                <td>{tx.branch}</td>
                <td>{tx.ifsc_code}</td>
                <td>{tx.account_holder_name}</td>
                <td>{tx.account_number}</td>
                <td>
                  {tx.generated_image ? (
                    <img
                      src={tx.generated_image}
                      alt="generated-img"
                      style={{ width: "60px", cursor: "pointer" }}
                      onClick={() => {
                        const filename = `${tx.creditorCode}_${
                          tx.debitorCode
                        }_${new Date(tx.date)
                          .toLocaleDateString("en-GB")
                          .replace(/\//g, "-")}.png`;
                        setSelectedImage({
                          url: tx.generated_image,
                          filename,
                        });
                      }}
                    />
                  ) : (
                    "No Image"
                  )}
                </td>
                <td style={{ minWidth: "150px" }}>
                  <textarea
                    className="form-control"
                    rows={2}
                    style={{ resize: "vertical" }}
                    defaultValue={tx.remarks || ""}
                    onBlur={(e) =>
                      handleRemarkChange(tx.id, e.target.value)
                    }
                    disabled={tx.status === "Committed"}
                  />
                </td>
                <td>
                  <div className="dropdown">
                    <button
                      className="btn btn-sm btn-secondary dropdown-toggle"
                      type="button"
                      data-bs-toggle="dropdown"
                    >
                      Actions
                    </button>
                    <ul className="dropdown-menu">
                      <li>
                        <button
                          className="dropdown-item"
                          onClick={() => handlePartPayment(tx)}
                        >
                          Part Payment
                        </button>
                      </li>
                      <li>
                        <button
                          className="dropdown-item"
                          onClick={() => updateStatus(tx.id, "Committed")}
                        >
                          Mark as Committed
                        </button>
                      </li>
                      <li>
                        <button
                          className="dropdown-item"
                          onClick={() => updateStatus(tx.id, "Cancelled")}
                        >
                          Mark as Cancelled
                        </button>
                      </li>
                      <li>
                        <button
                          className="dropdown-item"
                          onClick={() => updateStatus(tx.id, "Completed")}
                        >
                          Mark as Completed
                        </button>
                      </li>
                    </ul>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        <nav className="mt-3">
          <ul className="pagination justify-content-center">
            {Array.from({ length: totalPages }, (_, i) => (
              <li
                key={i}
                className={`page-item ${
                  currentPage === i + 1 ? "active" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              </li>
            ))}
          </ul>
        </nav>
      </div>

      {/* Image Preview Modal */}
      {selectedImage && (
        <div
          className="modal fade show"
          style={{
            display: "block",
            backgroundColor: "rgba(0,0,0,0.8)",
          }}
          onClick={() => setSelectedImage(null)}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-body text-center">
                <img
                  id="preview-img"
                  src={selectedImage.url}
                  alt="Full Preview"
                  style={{ maxWidth: "100%", height: "auto" }}
                />
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    const link = document.createElement("a");
                    link.href = selectedImage.url;
                    link.download =
                      selectedImage.filename || "transaction.png";
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                >
                  Download
                </button>
                <button
                  className="btn btn-warning"
                  onClick={() =>
                    copyImageToClipboard(selectedImage.url)
                  }
                >
                  Copy
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => setSelectedImage(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Generate Image Modal */}
      {showGenerate && (
        <div
          className="modal fade show"
          style={{
            display: "block",
            backgroundColor: "rgba(0,0,0,0.5)",
          }}
          onClick={() => setShowGenerate(false)}
        >
          <div
            className="modal-dialog modal-lg modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content p-3">
              <GenerateImage
                creditors={creditors}
                debitors={debitors}
                onTransactionSubmit={() => {
                  setShowGenerate(false);
                  fetchTransactions();
                }}
                prefillData={prefillData}
                onClose={() => setShowGenerate(false)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Transactions;